import React, {
  useState,
  useMemo,
  memo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS, TAGS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import CustomCreditQuery from "../queries/CustomCreditQuery";
import {
  FaSearch,
  FaTools,
  FaFilter,
  FaTimes,
  FaBalanceScale,
} from "react-icons/fa";
import { HiUser, HiOfficeBuilding } from "react-icons/hi";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

/* ===================== helpers ===================== */
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getQueryTagIds = (q) => {
  const fromObjects = (arr, key = "id") =>
    (arr || []).map((o) => toNum(o?.[key])).filter((n) => n !== null);

  if (Array.isArray(q?.Tags)) return fromObjects(q.Tags, "id");
  if (Array.isArray(q?.tags)) return fromObjects(q.tags, "id");
  if (Array.isArray(q?.QueryTags)) return fromObjects(q.QueryTags, "TagId");
  if (Array.isArray(q?.tagIds))
    return (q.tagIds || []).map(toNum).filter((n) => n !== null);
  if (Array.isArray(q?.tagsIds))
    return (q.tagsIds || []).map(toNum).filter((n) => n !== null);
  return [];
};

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const matchesSearch = (query, term) => {
  if (!term) return true;
  const t = normalize(term);
  return normalize(query?.name).includes(t);
};

// OR: contém ao menos uma tag selecionada
const matchesTagFilterOR = (query, selectedTagIds) => {
  if (!selectedTagIds.length) return true;
  const qTags = getQueryTagIds(query);
  return selectedTagIds.some((id) => qTags.includes(id));
};

// detecta tipo (CPF/CNPJ)
const detectType = (q) => {
  const t = q?.QueryCustomized?.parameterType;
  if (t === "CPF" || t === "CNPJ") return t;
  const name = (q?.name || "").toLowerCase();
  if (name.includes("cpf")) return "CPF";
  if (name.includes("cnpj") || name.includes("empresa")) return "CNPJ";
  return "UNKNOWN";
};

// animação painel de detalhes
const queryPanelVariants = {
  hidden: { opacity: 0, x: 24 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 220, damping: 22 },
  },
  exit: { opacity: 0, x: 16, transition: { duration: 0.16 } },
};

// animação dos cards (em viewport)
const cardVariants = {
  hidden: { opacity: 0, y: 4 },
  show: { opacity: 1, y: 0 },
};

/* ===================== Modal Genérico ===================== */
const Modal = ({ open, onClose, title, children }) => {
  const dialogRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          {/* Conteúdo */}
          <motion.div
            ref={dialogRef}
            className="relative mx-3 w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabeçalho */}
            <div className="bg-gradient-to-r from-blue-600 to-green-500 px-5 py-4 text-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-4.5 h-4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20.59 13.41L11 3.83A2 2 0 009.59 3H4a1 1 0 00-1 1v5.59A2 2 0 003.83 11l9.58 9.59a2 2 0 002.83 0l4.35-4.35a2 2 0 000-2.83z" />
                      <circle cx="7.5" cy="7.5" r="1.5" />
                    </svg>
                  </span>
                  <div className="leading-tight">
                    <h2
                      id="modal-title"
                      className="text-sm sm:text-base font-semibold"
                    >
                      {title}
                    </h2>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md px-3 py-1.5 text-[12px] bg-white/10 hover:bg-white/20 transition"
                  aria-label="Fechar"
                  title="Fechar"
                >
                  Fechar
                </button>
              </div>
            </div>
            {/* Corpo */}
            <div className="p-4 sm:p-5 max-h-[70vh] overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

/* ===================== Card da Consulta ===================== */
const QueryCard = memo(function QueryCard({
  query,
  index,
  onOpenModal,
  allTags,
  handleQueryClick,
}) {
  const { user } = useApplication();
  if (query?.status == 1) {
    return (
      <motion.div
        key={query.id}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={cardVariants}
        transition={{
          duration: 0.22,
          ease: "easeOut",
          delay: Math.min(index * 0.03, 0.18),
        }}
        onClick={() => handleQueryClick(query)}
        className="relative group cursor-pointer rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      >
        <div className="absolute -top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100">
          <div className="bg-gradient-to-r from-blue-600 to-green-400 text-white text-[11px] font-semibold px-3 py-[2px] rounded-full shadow">
            CONSULTAR AGORA
          </div>
        </div>

        <div className="relative z-5 flex flex-col gap-1">
          <h3
            className="truncate text-sm font-semibold text-gray-900"
            title={query.name}
          >
            {query.name?.toUpperCase()}
          </h3>
          <span className="text-[11px] text-gray-500">
            {user?.Company?.showPriceQuery && query?.price
              ? `${formatToReal(query?.price)}  - `
              : ""}{" "}
            Consulta de crédito
          </span>

          {(() => {
            const tagIds = getQueryTagIds(query);
            if (!tagIds.length) return null;
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenModal(query);
                }}
                className="w-fit text-[11px] text-blue-700 hover:text-blue-800 underline decoration-dotted cursor-pointer whitespace-nowrap"
                title="Ver todos os campos retornados"
              >
                Ver tudo o que retorna
              </button>
            );
          })()}
        </div>

        <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-blue-600 to-green-400 transition-all duration-300 opacity-40 group-hover:opacity-80 rounded-b-lg" />
      </motion.div>
    );
  }

  // estado != 1 → manutenção
  return (
    <motion.div
      key={query.id}
      initial={{ opacity: 0, scale: 0.97, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className="bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300 rounded-lg p-4 shadow-sm cursor-not-allowed relative group overflow-hidden opacity-50 pointer-events-none"
    >
      <div className="flex flex-col gap-1 relative z-10">
        <div className="flex items-center gap-2">
          <FaTools className="text-gray-500 text-sm" />
          <h3
            className="text-sm font-semibold text-gray-600 truncate"
            title={query.name}
          >
            {query.name?.toUpperCase()}
          </h3>
        </div>
        <span className="text-[11px] text-gray-500">
          Consulta em manutenção
        </span>
      </div>
      <FaTools className="absolute bottom-2 right-2 text-gray-300 text-4xl z-0" />
      <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gray-400 rounded-b-lg z-0" />
    </motion.div>
  );
});

/* ===================== Principal ===================== */
const QueriesCreditTable = () => {
  const navigate = useNavigate();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);

  // abas: ALL | CPF | CNPJ
  const [activeTab, setActiveTab] = useState("ALL");

  // modal: guarda a query cujas tags vamos exibir
  const [modalQuery, setModalQuery] = useState(null);

  // filtro de tags
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);

  // queries do usuário
  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items.data;
    }
  );

  // tags
  const { isLoading: isLoadingTags, data: tagsPayload } = useQuery(
    ["credit-tags"],
    async () => {
      const { data } = await axios.get(TAGS.GET_ALL_SIMPLE);
      return Array.isArray(data?.data) ? data.data : data;
    },
    { staleTime: 1000 * 60 * 10 }
  );

  const allTags = useMemo(() => tagsPayload || [], [tagsPayload]);

  const tagCounts = useMemo(() => {
    const counts = new Map();
    (userQueryPermissions || []).forEach((q) => {
      getQueryTagIds(q).forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [userQueryPermissions]);

  const filteredTags = useMemo(() => {
    const s = tagSearch.trim().toLowerCase();
    if (!s) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(s));
  }, [allTags, tagSearch]);

  // 1) aplica busca + OR de tags
  const filteredQueriesBase = useMemo(() => {
    return (userQueryPermissions || []).filter(
      (q) =>
        matchesSearch(q, searchTerm) && matchesTagFilterOR(q, selectedTagIds)
    );
  }, [userQueryPermissions, searchTerm, selectedTagIds]);

  // 2) filtra por aba
  const filteredByTab = useMemo(() => {
    if (activeTab === "ALL") return filteredQueriesBase;
    return filteredQueriesBase.filter((q) => detectType(q) === activeTab);
  }, [filteredQueriesBase, activeTab]);

  // 3) filtra somente categoria Crédito (id=1), status válido e não adicionais
  const sortedQueries = useMemo(
    () =>
      filteredByTab
        ?.filter((q) => {
          const isAdditional =
            typeof q.isAdditional === "boolean"
              ? q.isAdditional
              : Number(q.isAdditional) === 1;
          return q.categoryId === 1 && q.status != -1 && !isAdditional;
        })
        ?.sort((a, b) => a?.name?.localeCompare(b.name)),
    [filteredByTab]
  );

  // contadores por aba
  const tabCounts = useMemo(() => {
    const all = (userQueryPermissions || []).filter((q) => {
      const isAdditional =
        typeof q.isAdditional === "boolean"
          ? q.isAdditional
          : Number(q.isAdditional) === 1;
      return q.categoryId === 1 && q.status != -1 && !isAdditional;
    });
    const cpf = all.filter((q) => detectType(q) === "CPF");
    const cnpj = all.filter((q) => detectType(q) === "CNPJ");
    return { ALL: all.length, CPF: cpf.length, CNPJ: cnpj.length };
  }, [userQueryPermissions]);

  const toggleTag = (id) => {
    const nid = Number(id);
    setSelectedTagIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  const selectAllFiltered = () => {
    const onlyIds = (filteredTags || []).map((t) => Number(t.id));
    setSelectedTagIds((prev) => Array.from(new Set([...prev, ...onlyIds])));
  };
  const clearAllTags = () => setSelectedTagIds([]);

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setSelectedPrice(query.price || 0);
    setIsListVisible(false);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const prefersReduced = useReducedMotion();

  // dados da modal: tags da query selecionada
  const modalTagObjects = useMemo(() => {
    if (!modalQuery) return [];
    const ids = getQueryTagIds(modalQuery);
    return ids.map((id) => allTags.find((t) => t.id === id)).filter(Boolean);
  }, [modalQuery, allTags]);

  const groupedModalTags = useMemo(() => {
    const groups = modalTagObjects.reduce((acc, t) => {
      const letter = (t.name?.[0] || "#").toUpperCase();
      (acc[letter] ||= []).push(t);
      return acc;
    }, {});
    const letters = Object.keys(groups).sort();
    return { groups, letters };
  }, [modalTagObjects]);

  if (!userQueryPermissions?.length && !isLoading)
    return <ForbiddenQueriesPermission />;

  const activeFilters = selectedTagIds.length > 0 || !!searchTerm;

  return (
    <div className="w-full px-1 md:px-12 pt-4 min-h-screen">
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Consultas de Crédito
        </h1>
        <div className="w-16 h-[3px] bg-gradient-to-r from-blue-600 to-green-400 mt-2 rounded" />
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm sm:text-base text-gray-600">
            Use as abas, a busca e os campos para encontrar a consulta ideal.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/queries/compare/pick")}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              title="Ir para seleção de consultas"
            >
              <FaBalanceScale />
              Comparar Consultas
            </button>
          </div>
        </div>
      </div>

      {isListVisible ? (
        <>
          {/* Filtros */}
          <div className="mb-4 space-y-3">
            {/* Busca por nome */}
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar consultas de crédito..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600/30 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                <FaSearch size={18} />
              </div>

              {activeFilters && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedTagIds([]);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
                  title="Limpar filtros"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Acordeão de Tags (expansível) */}
            {(() => {
              const selectedTagsLocal = (allTags || []).filter((t) =>
                selectedTagIds.includes(t.id)
              );

              return (
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                  {/* Cabeçalho */}
                  <button
                    type="button"
                    onClick={() => setIsTagFilterOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-blue-600 to-green-400 text-white hidden md:inline-flex">
                        <FaFilter size={12} />
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        Filtrar por campos
                      </span>
                      <span className="text-xs text-gray-500">
                        ({selectedTagIds.length} selecionada
                        {selectedTagIds.length === 1 ? "" : "s"})
                      </span>

                      {/* prévia (máx 3) */}
                      {!!selectedTagsLocal.length && (
                        <div className="hidden md:flex items-center gap-1 ml-2">
                          {selectedTagsLocal.slice(0, 3).map((t) => (
                            <span
                              key={t.id}
                              className="text-[10px] px-2 py-[2px] rounded-full bg-gray-50 text-gray-700 border border-gray-200 whitespace-nowrap"
                              title={t.name}
                            >
                              {t.name}
                            </span>
                          ))}
                          {selectedTagsLocal.length > 3 && (
                            <span className="text-[10px] px-2 py-[2px] rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                              +{selectedTagsLocal.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTagIds([]);
                          setTagSearch("");
                        }}
                        className="px-2 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 hidden md:block"
                        disabled={!selectedTagIds.length && !tagSearch}
                        title="Limpar filtro"
                      >
                        Limpar
                      </button>
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          isTagFilterOpen ? "rotate-180" : ""
                        }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 9l6 6 6-6"
                        />
                      </svg>
                    </div>
                  </button>

                  {isTagFilterOpen && (
                    <div className="px-4 pb-4">
                      {/* busca + ações */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="relative w-full sm:max-w-xs">
                          <FaSearch
                            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                            size={12}
                          />
                          <input
                            type="text"
                            placeholder="Buscar campo..."
                            value={tagSearch}
                            onChange={(e) => setTagSearch(e.target.value)}
                            className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600/30"
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={selectAllFiltered}
                            className="px-2 py-1 rounded-md border border-gray-300 text-xs hover:bg-gray-50 disabled:opacity-50"
                            disabled={!filteredTags.length}
                          >
                            Selecionar filtradas
                          </button>
                          <button
                            type="button"
                            onClick={clearAllTags}
                            className="px-2 py-1 rounded-md border border-gray-300 text-xs hover:bg-gray-50 disabled:opacity-50"
                            disabled={!selectedTagIds.length}
                          >
                            Limpar seleção
                          </button>
                        </div>
                      </div>

                      {/* selecionadas (chips) */}
                      {!!selectedTagsLocal.length && (
                        <div className="mt-3 -mx-1 overflow-x-auto">
                          <div className="flex items-center gap-1.5 px-1 py-1 no-scrollbar">
                            {selectedTagsLocal.map((t) => (
                              <span
                                key={t.id}
                                className="inline-flex items-center gap-1.5 text-[11px] leading-4 px-2.5 py-1 rounded-full bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50"
                                title={t.name}
                              >
                                <span className="truncate max-w-[42vw] sm:max-w-[220px]">
                                  {t.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => toggleTag(t.id)}
                                  className="rounded-full w-4 h-4 flex items-center justify-center border border-gray-300 text-gray-600 hover:bg-gray-100 shrink-0"
                                  aria-label={`Remover ${t.name}`}
                                  title="Remover"
                                >
                                  <FaTimes className="scale-90" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* lista de tags */}
                      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 max-h-56 overflow-auto">
                        <div className="grid [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))] gap-2">
                          {(filteredTags || []).map((tag) => {
                            const active = selectedTagIds.includes(tag.id);
                            const count = tagCounts.get(tag.id) || 0;
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                className={`w-full rounded-lg border px-2.5 py-2 text-left text-[12px] sm:text-sm transition-all
                                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30
                                  ${
                                    active
                                      ? "bg-white text-blue-700 border-blue-600 shadow"
                                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                                  }`}
                                title={`${tag.name} • ${count} consulta(s)`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate" title={tag.name}>
                                    {tag.name}
                                  </span>
                                  <span
                                    className={`shrink-0 inline-flex items-center justify-center h-4 min-w-[18px] px-1.5 text-[10px] rounded-full
                                      ${
                                        active
                                          ? "bg-blue-50 text-blue-700 border border-blue-600/40"
                                          : "bg-gray-50 text-gray-600 border border-gray-200"
                                      }`}
                                  >
                                    {count}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                          {!filteredTags?.length && (
                            <span className="col-span-full text-xs text-gray-500">
                              Nenhuma tag encontrada.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Resultado */}
          <div className="flex items-center justify-between mb-2">
            {/* Abas (Todas | CPF | CNPJ) */}
            <div className="">
              <div className="inline-flex rounded-2xl bg-white p-1 border border-gray-200 shadow-sm">
                {[
                  {
                    key: "ALL",
                    label: "Todas",
                    icon: null,
                    count: tabCounts.ALL,
                  },
                  {
                    key: "CPF",
                    label: "CPF",
                    icon: HiUser,
                    count: tabCounts.CPF,
                  },
                  {
                    key: "CNPJ",
                    label: "CNPJ",
                    icon: HiOfficeBuilding,
                    count: tabCounts.CNPJ,
                  },
                ].map(({ key, label, icon: Icon, count }) => {
                  const active = activeTab === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={`relative inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm transition
                      ${
                        active
                          ? "bg-blue-900 text-white shadow border border-gray-200"
                          : "text-gray-600 hover:text-gray-800"
                      }
                    `}
                      aria-pressed={active}
                    >
                      {Icon ? <Icon className="text-[16px]" /> : null}
                      <span className="font-medium">{label}</span>
                      <span
                        className={`ml-1 inline-flex items-center justify-center h-5 min-w-[22px] px-1.5 rounded-full text-[11px]
                        ${
                          active
                            ? "bg-gray-100 text-gray-800"
                            : "bg-white/70 text-gray-700 border border-gray-200"
                        }
                      `}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <span className="text-sm text-gray-600 hidden md:block">
              {sortedQueries?.length || 0} consulta
              {sortedQueries?.length === 1 ? "" : "s"} encontrada
              {activeFilters ? " com os filtros atuais." : "."}
            </span>
          </div>

          {/* Grid de Cards */}
          <div
            style={{ minHeight: "300px" }}
            className="overflow-hidden relative items-start grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200"
          >
            {(isLoading || isLoadingTags) && <DrawerLoading />}
            {sortedQueries?.map((query, index) => (
              <QueryCard
                key={query.id}
                query={query}
                index={index}
                onOpenModal={setModalQuery}
                allTags={allTags}
                handleQueryClick={handleQueryClick}
              />
            ))}
            {!sortedQueries?.length && !isLoading && (
              <div className="col-span-full text-center text-sm text-gray-500 py-10">
                Nenhuma consulta corresponde aos filtros aplicados.
              </div>
            )}
          </div>
        </>
      ) : (
        <div>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-blue-700 hover:text-blue-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          <motion.div
            variants={queryPanelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <CustomCreditQuery
              query={selectedQuery}
              selectedPrice={selectedPrice}
              userQueryPermissions={userQueryPermissions}
            />
          </motion.div>
        </div>
      )}

      {/* MODAL: O que retorna */}
      <Modal
        open={!!modalQuery}
        onClose={() => setModalQuery(null)}
        title={
          modalQuery
            ? `O que retorna — ${modalQuery.name?.toUpperCase()}`
            : "O que retorna"
        }
      >
        {(() => {
          const modalTagObjects = (() => {
            if (!modalQuery) return [];
            const ids = getQueryTagIds(modalQuery);
            return ids
              .map((id) => allTags.find((t) => t.id === id))
              .filter(Boolean);
          })();

          if (!modalTagObjects.length) {
            return (
              <div className="text-sm text-gray-600">
                Nenhum campo encontrado.
              </div>
            );
          }

          const grouped = modalTagObjects.reduce((acc, t) => {
            const letter = (t.name?.[0] || "#").toUpperCase();
            (acc[letter] ||= []).push(t);
            return acc;
          }, {});
          const letters = Object.keys(grouped).sort();

          return (
            <div className="space-y-4">
              <div className="text-[12px] text-gray-600">
                {modalTagObjects.length} item
                {modalTagObjects.length > 1 ? "s" : ""}
              </div>

              {letters.map((letter) => (
                <section key={letter} className="space-y-2">
                  <div className="sticky top-0 z-10 -mx-4 px-4 py-1.5 bg-white/95 backdrop-blur border-b border-gray-100 text-[11px] font-semibold tracking-wide text-gray-600 flex items-center justify-between">
                    <span>{letter}</span>
                    <span className="text-[10px] font-normal text-gray-400">
                      {grouped[letter].length} item
                      {grouped[letter].length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {grouped[letter].map((t) => (
                      <li
                        key={t.id}
                        className="group flex items-start gap-2 rounded-lg px-3 py-2 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-blue-200 transition-colors"
                        title={t.name}
                      >
                        <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-green-400 shrink-0" />
                        <span className="text-[11px] leading-5 text-gray-800 break-words">
                          {t.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default QueriesCreditTable;
